// IMP-446 (#414): Export 컬럼 선택 공통 SSoT (순수 util).
//
// 목록 화면의 Export 경로 중 체크박스 선택(RayTable)과 전체 Export(AdminProductList) 두 경로는
// "후보 목록 구성 · 기본 체크값 · 선택 결과 → 최종 컬럼" 계산을 이 모듈 하나로 통일한다.
// 경로별로 컬럼 필터를 각자 구현하면 같은 규칙이 두 벌이 된다(BUG-153 유형의 회귀 원인).
// (RayTableExport 는 도달 불가 경로라 미적용 — 살아나면 이 모듈로 편입한다.)
//
// [계약 — IMP-444 Spec §5.1]
// 1. 리스트 열 선택(`adminheaders.<prefix>` 의 visible/order)이 단일 소유자다. 이 모듈은 읽기만 한다.
// 2. 기본 체크값은 리스트 표시 상태를 그대로 복사한다 → defaultExportSelection 이
//    headerPrefs.visibleHeaderColumns 를 그대로 재사용한다(판정 로직 복제 금지).
// 3. 후보 목록은 표시 필터를 거치지 않은 전체 columns 다 — 숨김(visible:false) 컬럼도 후보에 남는다.
//    컬럼 목록에 아직 넣지 않은 '미추가 컬럼'은 범위 밖이며, 커스텀 헤더에서 추가하는 경로를 쓴다.
// 4. write-back 금지 — 이 모듈은 어떤 저장 payload 도 만들지 않는다.
//
// React/i18n/axios 비의존. 부수효과 없음(console.warn 제외). 어떤 입력에도 throw 하지 않는다.

import { visibleHeaderColumns } from './headerPrefs';

// 화면 전용 컬럼이라 Export 파일에 담을 수 없는 타입. 후보 단계에서 걸러 낸다.
export const EXPORT_EXCLUDED_TYPES = ['BUTTON', 'DEALER_MATCH'];

/**
 * Export 모달의 후보 목록을 만든다.
 *
 * 입력은 표시 필터를 거치지 않은 전체 columns 여야 한다(숨김 컬럼 포함 — 계약 3).
 * Export 불가 타입과 name 없는 항목만 제외하고 입력 순서를 그대로 유지하며,
 * 같은 name 이 중복되면 먼저 나온 것만 남긴다.
 */
export const exportCandidateColumns = (columns) => {
    if (!Array.isArray(columns)) return [];

    const seen = new Set();
    return columns.filter((col) => {
        if (!col || typeof col.name !== 'string' || col.name === '') return false;
        if (EXPORT_EXCLUDED_TYPES.includes(col.type)) return false;
        if (seen.has(col.name)) return false;
        seen.add(col.name);
        return true;
    });
};

/**
 * 모달을 열었을 때의 기본 체크값(name 배열)을 만든다 — 계약 2.
 *
 * 판정은 visibleHeaderColumns 에 위임하므로, "전 항목 숨김이면 입력 전체 반환 + warn" 폴백도
 * 그대로 따라온다(후보가 전부 숨김이면 전 후보가 기본 체크된다).
 */
export const defaultExportSelection = (columns) =>
    visibleHeaderColumns(exportCandidateColumns(columns)).map((col) => col.name);

/**
 * 사용자가 고른 이름들로 실제 Export 에 쓸 컬럼 배열을 만든다.
 *
 * 순서는 selectedNames 가 아니라 columns 원래 순서를 따른다(리스트와 파일의 컬럼 순서 일치).
 * selectedNames 가 배열이 아니면 기본 체크값을 쓴다.
 * 선택 결과가 비면 헤더 0개짜리 파일이 나가므로 기본 체크값으로 되돌리고 warn 을 남긴다
 * (silent-drop 금지 — 프로젝트 CLAUDE.md §7).
 */
export const selectExportColumns = (columns, selectedNames) => {
    const candidates = exportCandidateColumns(columns);
    if (candidates.length === 0) return [];

    const fallback = () => {
        const names = new Set(defaultExportSelection(columns));
        return candidates.filter((col) => names.has(col.name));
    };

    if (!Array.isArray(selectedNames)) return fallback();

    const picked = new Set(selectedNames);
    const selected = candidates.filter((col) => picked.has(col.name));
    if (selected.length === 0) {
        console.warn('[exportColumns] no column selected — falling back to list visibility', {
            handler: 'selectExportColumns',
            total: candidates.length,
            reason: 'selectedNames matched no candidate column',
        });
        return fallback();
    }
    return selected;
};
